import { trackCampaignPage } from '~/utils/adTracking';
import { h, render } from 'preact';
import Episodes from '~/components/Episodes/Episode';
import Player from '~/components/Player/Player';
import { mostRecentStore } from '../../mostRecentStore';
import MostRecent from '~/components/MostRecent/MostRecent';
import Clipboard from 'clipboard';
import PopularPodcasts from '~/components/PopularPodcasts';
import favorites from '~/favoritesStore';
import Notification from '~/notification';
import { commitOptimisticLike } from '~/pages/Podcast/optimisticLike';
import { playLatestEpisode } from '~/pages/Podcast/playLatest';
import { getProgress, player as s } from '../../player/store';
import type { Episode } from '~/types/podcast';
import type { Podcast } from '@/types/podcast';
import {
    clearPendingLike,
    fetchPodcastLikeStatus,
    getPendingLike,
    likePodcast,
    setPendingLike,
    unlikePodcast,
} from '~/podcastLikesAccount';
import { loadPublicAuthState } from '~/publicAuth';

declare global {
    interface Window {
        __INITIAL_STATE:
            | {
                  notfound: true;
                  podcast: never;
                  podcastUrl: never;
                  episode?: never;
                  episodeUrl?: never;
              }
            | {
                  notfound?: undefined;
                  podcast: Podcast;
                  podcastUrl: string;
                  auth: {
                      loginUrl: string;
                  };
                  verify: {
                      eligible: boolean;
                      url: string;
                  };
                  episode?: Episode;
                  episodeUrl?: string;
              };
    }
}

function podcast(/** match */) {
    // debuggin issues with cdn and builds
    // Todo: safari caches webpage on navigate and when navigate back
    // the player is a in a playing mode but no audio is.
    // We should try to reset it to  be paused
    window.onpageshow = function (event) {};
    /* Player in overlay view / BIG */
    window.addEventListener(
        'player:big.enter',
        () => {
            if (!document.documentElement.classList.contains('player--is-big')) {
                document.documentElement.className += ' player--is-big';
            }
        },
        false,
    );
    window.addEventListener(
        'player:big.leave',
        () => {
            if (document.documentElement.classList.contains('player--is-big')) {
                document.documentElement.classList.remove('player--is-big');
            }
        },
        false,
    );

    const shareButton = document.querySelector('.podcast-share');
    const shareBoard = document.querySelector('#share_pod');
    const closeShareButton = shareBoard?.querySelector('.close-share');
    const clipboard = new Clipboard('#copy_url');
    // @ts-expect-error - clipboard missing types for this api
    clipboard.on('success', () => {
        const copyButton = shareBoard?.querySelector<HTMLSpanElement>('#copy_url > span');
        const rawText = copyButton?.innerText;

        if (copyButton && rawText) {
            copyButton.innerText = 'Kopierad!';
            setTimeout(() => {
                shareBoard?.classList.add('hidden');
                copyButton.innerText = rawText;
            }, 400);
        }
    });

    closeShareButton?.addEventListener('click', (e) => {
        e.stopPropagation();
        shareBoard?.classList.add('hidden');
    });

    shareButton?.addEventListener('click', (e) => {
        e.stopPropagation();
        shareBoard?.classList.remove('hidden');
    });

    document.addEventListener('click', (e: MouseEvent) => {
        if (shareBoard?.classList.contains('hidden')) {
            return;
        }

        if (!shareBoard?.contains(e.target as Node)) {
            shareBoard?.classList.add('hidden');
        }
    });

    if (typeof window.__INITIAL_STATE === 'undefined') {
        return;
    }
    if (window.__INITIAL_STATE.notfound === true) return;

    const podcast = window.__INITIAL_STATE.podcast;
    const loginUrl = window.__INITIAL_STATE.auth.loginUrl;
    const verify = window.__INITIAL_STATE.verify;

    const likeButton = document.querySelector('.js-podcast-like');
    const [animatedHeart, baseHeart] = likeButton?.querySelectorAll('svg');
    const verifySlot = document.querySelector('.js-podcast-verify-slot');

    const notification = new Notification(document.querySelector('.js-liked-podcast-toast'), {
        hideAfter: 8000,
    });
    const errorNotification = new Notification(
        document.querySelector('.js-podcast-like-error-toast'),
        {
            hideAfter: 8000,
        },
    );

    let isAuthenticated = false;
    let accountLikesEnabled = false;
    let isLiked = false;
    let likeRequestInFlight = false;
    let viewerStateLoaded = false;
    let viewerStatePromise: Promise<void> | null = null;

    const setLikeState = (liked: boolean, { animate = false } = {}) => {
        isLiked = liked;

        if (!baseHeart || !animatedHeart) {
            return;
        }

        baseHeart.classList.toggle('fill-primary', liked);

        if (!liked) {
            animatedHeart.classList.remove('animate-ping-once');
            animatedHeart.classList.add('hidden');
            return;
        }

        animatedHeart.classList.remove('hidden');
        if (animate) {
            animatedHeart.classList.remove('animate-ping-once');
            void animatedHeart.getBoundingClientRect();
            animatedHeart.classList.add('animate-ping-once');
        }
    };

    const loadViewerState = async () => {
        if (viewerStateLoaded) {
            return;
        }

        if (!viewerStatePromise) {
            viewerStatePromise = (async () => {
                const authState = await loadPublicAuthState();

                isAuthenticated = authState.authenticated;
                accountLikesEnabled = authState.features.accountLikes;
                updateVerifyCallToAction(authState.features.podcastVerification);

                if (!accountLikesEnabled) {
                    setLikeState(favorites.has(podcast.itunes_id));
                    return;
                }

                if (!isAuthenticated) {
                    setLikeState(false);
                    return;
                }

                try {
                    await syncLegacyLikesAndPendingActions();
                } catch (error) {
                    console.warn('Legacy likes sync failed:', error);
                }

                try {
                    const liked = await fetchPodcastLikeStatus(podcast.itunes_id);
                    setLikeState(liked);
                } catch (error) {
                    console.warn('Like status check failed:', error);
                }
            })()
                .then(() => {
                    viewerStateLoaded = true;
                })
                .finally(() => {
                    viewerStatePromise = null;
                });
        }

        await viewerStatePromise;
    };

    const syncLegacyLikesAndPendingActions = async () => {
        if (!isAuthenticated) {
            return;
        }

        const pendingLike = getPendingLike();
        if (!pendingLike) {
            return;
        }

        await likePodcast(pendingLike);
        if (pendingLike === podcast.itunes_id) {
            setLikeState(true, { animate: true });
            notification.show();
        }
        clearPendingLike();
    };

    const toggleLegacyLike = () => {
        if (favorites.has(podcast.itunes_id)) {
            setLikeState(false);
            favorites.remove(podcast.itunes_id);
            return;
        }

        if (favorites.getAll().length <= 0) {
            notification.show();
        }

        favorites.add(podcast.itunes_id, 'podcast');
        errorNotification.hide();
        setLikeState(true, { animate: true });
    };

    const redirectToLogin = () => {
        const targetUrl = new URL(loginUrl, window.location.origin);
        targetUrl.searchParams.set('intended', window.location.pathname + window.location.search);

        window.location.assign(targetUrl.toString());
    };

    const updateVerifyCallToAction = (canVerify: boolean) => {
        if (!verifySlot) {
            return;
        }

        if (!verify.eligible || !canVerify) {
            verifySlot.innerHTML = '';
            return;
        }

        verifySlot.innerHTML = `
      <div class="border-t mt-2 pt-2">
        <a href="${verify.url}" class="flex items-center text-sm text-brand-900 hover:text-brand-700 transition">
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
          </svg>
          <span>Är du ägaren? Verifiera</span>
        </a>
      </div>
    `;
    };

    setLikeState(false);
    updateVerifyCallToAction(false);

    void loadViewerState();

    likeButton?.addEventListener('click', async (e) => {
        e.preventDefault();

        if (likeRequestInFlight) {
            return;
        }

        await loadViewerState();

        if (!accountLikesEnabled) {
            toggleLegacyLike();
            return;
        }

        if (!isAuthenticated) {
            setPendingLike(podcast.itunes_id);
            redirectToLogin();
            return;
        }

        likeRequestInFlight = true;

        try {
            await commitOptimisticLike({
                isLiked,
                setLikeState,
                like: () => likePodcast(podcast.itunes_id),
                unlike: () => unlikePodcast(podcast.itunes_id),
                onLiked: () => {
                    errorNotification.hide();
                    notification.show();
                },
                onError: () => {
                    notification.hide();
                    errorNotification.show();
                },
            });
        } catch {
            // The optimistic state has already been rolled back.
        } finally {
            likeRequestInFlight = false;
        }
    });

    const listenButton = document.querySelector('button.podcast_listen');
    if (listenButton) {
        listenButton.addEventListener('click', (e) => {
            playLatestEpisode(podcast.meta.episodes[0], podcast, s, getProgress);
            e.preventDefault();
        });
    }

    const adDark = document.querySelector('.ad-dark');
    if (adDark) {
        const { x, y } = adDark.getBoundingClientRect();
        if (x === 0 && y === 0 && window.innerWidth > 1200) {
            const bc = document.querySelector('.bc');
            if (bc) {
                const pb = window.getComputedStyle(bc).getPropertyValue('padding-bottom');

                bc.setAttribute('style', `padding-bottom: ${+pb.replace('px', '') - 200}px`);
            }
        }
    }

    mostRecentStore.load().trackVisit(podcast.itunes_id);

    const mountPlayerNode = document.getElementById('playerplace');
    const firstEpisode = podcast.meta.episodes[0];
    if (mountPlayerNode && firstEpisode) {
        s.mount(firstEpisode, podcast, getProgress(firstEpisode, podcast.itunes_id).currentSeek);

        render(h(Player, { store: s }), mountPlayerNode);
    }

    const episodesNode = document.querySelector('.episodes');
    if (episodesNode?.parentElement) {
        episodesNode.innerHTML = '';
        render(
            h(Episodes, {
                store: s,
                podcast,
                title: podcast.title,
                header: 'Fler avsnitt av',
                podcastUrl: window.__INITIAL_STATE.podcastUrl,
            }),
            episodesNode.parentElement,
        );
    }
    const popularPodcastsNode = document.querySelector('#popularpodcasts');
    if (popularPodcastsNode) {
        render(
            h(PopularPodcasts, {
                title: 'Populärt på Poddtoppen just nu',
                slot: 'popular.horizontal.podcast',
                action: '/populara',
            }),
            popularPodcastsNode,
        );
    }

    const mostRecentNode = document.querySelector('#mostrecent');
    if (mostRecentNode) {
        render(
            h(MostRecent, {
                title: 'Senast besökta',
            }),
            mostRecentNode,
        );
    }
}

podcast();

if (window.__INITIAL_STATE && !window.__INITIAL_STATE.notfound) {
    trackCampaignPage(Number(window.__INITIAL_STATE.podcast.itunes_id));
}
